import styled from "@emotion/styled";
import { GenAI24Icon } from "@kaltura/ds-react-icons";
import { Button, Typography } from "@kaltura/ds-react-components";
import { BannerImage } from "./BannerImage";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { Theme } from "@mui/material/styles";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { useContext } from "react";
import { ConfigContext } from "@kaltura/mediaspace-shared-data-kms-config/ConfigContext";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";

export interface BannerProps {
    title: string;
    description: string;
    buttonText: string;
    buttonLink: string;
}

export const Banner = ({ title, description, buttonText, buttonLink }: BannerProps) => {
    const isSmallScreen = useMediaQuery<Theme>((theme) => theme.breakpoints.down(theme.breakpoints.values.md));
    const sendButtonAnalytics = useButtonAnalytics();
    const configContext = useContext(ConfigContext);
    const isKaf = configContext.application?.isKAF;

    const sendAnalytics = () => {
        sendButtonAnalytics(
            `open genie – ${isKaf ? "Channel" : "Course Media Gallery"}`,
            ButtonClickAnalyticsType.NAVIGATE,
            undefined,
            "Genie",
            "Genie module"
        );
    };

    return (
        <Container>
            <LeftColumn>
                <Typography component={"h2"} variant={isSmallScreen ? "h3" : "h2"}>
                    {title}
                </Typography>
                <StyledDescription>{description}</StyledDescription>
                <div>
                    <Button startIcon={<GenAI24Icon />} href={buttonLink} onClick={sendAnalytics}>
                        {buttonText}
                    </Button>
                </div>
            </LeftColumn>
            <RightColumn>
                <BannerImage isSmallScreen={isSmallScreen} />
            </RightColumn>
        </Container>
    );
};

const Container = styled("div")(({ theme }) => ({
    borderRadius: theme.kaltura.shape.roundness2,
    /**
     * The Linear gradient of the same color twice needed for the gradient border trick.
     * It creates a solid fill for the inner area (padding-box), while the second gradient applies only to the border (border-box).
     * Using the same color twice ensures the inside stays a flat color (not gradient) and prevents the border gradient from bleeding into the content area.
     */
    background: `
    linear-gradient(${theme.kaltura.palette.surfaces.paper}, ${theme.kaltura.palette.surfaces.paper}) padding-box,
    linear-gradient(90deg, #5BC686 0%, #FF9DFF 50%, #006EFA 100%) border-box
  `,
    paddingLeft: theme.spacing(5),
    width: "100%",
    border: "2px solid transparent",
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    boxSizing: "border-box",

    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        textAlign: "center",
        padding: theme.spacing(3),
        gap: 0,
    },
}));

const LeftColumn = styled("div")(({ theme }) => ({
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(3, 0),
    [theme.breakpoints.down("md")]: {
        order: 2,
        alignItems: "center",
        padding: 0,
    },
}));

const RightColumn = styled("div")({
    flex: "0 0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});

const StyledDescription = styled("p")({
    marginTop: 0,
});
